import React, {useState} from 'react';
import {LexicalEditor} from 'lexical';
import {INSERT_IMAGE_COMMAND} from '../commands';
import {useDropzone} from '../../../../../features/Form/components/FormViewImageField/useDropzone';
import {texts} from '../../../../../features/Form/components/HTMLFormEditor/Editor/texts';
import {
    Button,
    Flex,
    Icon,
    Input,
    PriorityIcon,
    ToolbarIcons
} from '@priority-software/priority-style-react';
import {ChatBoxButton} from '../../../../Common/Chatbox/ChatBox';
import {Uploader} from '../../../../FileUploader/uploader';
import styles from './styles.module.scss';

enum UploadMode {
    URL = 'url',
    FILE = 'file'
}

export function InsertImageDialog({
    activeEditor,
    onClose
}: {
    activeEditor: LexicalEditor;
    onClose: () => void;
}): JSX.Element {
    const [mode, setMode] = useState<null | UploadMode>(UploadMode.FILE);
    const [src, setSrc] = useState('');
    const [fileName, setFileName] = useState<string>('');

    const loadImage = async (files: File[] | null) => {
        const reader = new FileReader();

        reader.onload = function () {
            if (typeof reader.result === 'string') {
                setSrc(reader.result);
            }
            return '';
        };

        if (files !== null && files.length > 0) {
            const file = files[0];
            reader.readAsDataURL(file);
            setFileName(file.name);
        }
    };

    const {dropzoneRef, handleDrop, onDragLeave, onDragStart} = useDropzone({
        setFile: loadImage,
        hasData: !!src
    });

    const insertImage = () => {
        activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, {altText: '', src});
        onClose();
    };

    const clearData = () => {
        setSrc('');
        setFileName('');
    };

    const toggleMode = e => {
        setMode(e.currentTarget.id as UploadMode);
        clearData();
    };

    return (
        <Flex direction="column" alignItems={'stretch'} style={{gap: 16}}>
            <Flex style={{gap: 16}}>
                <ChatBoxButton
                    id={UploadMode.FILE}
                    active={mode === UploadMode.FILE}
                    onClick={toggleMode}
                    value={UploadMode.FILE}
                    icon={ToolbarIcons.toolbarImage}
                    text={texts.file_label}
                />
                <ChatBoxButton
                    id={UploadMode.URL}
                    active={mode === UploadMode.URL}
                    onClick={toggleMode}
                    value={UploadMode.URL}
                    icon={ToolbarIcons.toolbarLinkChain}
                    text={texts.link_dialog_link_label}
                />
            </Flex>

            {mode === UploadMode.URL && (
                <Input
                    autoFocus={true}
                    type={'text'}
                    label={
                        <span className="pui-label-required">{texts.link_dialog_link_label}</span>
                    }
                    prefixIcon={<Icon icon={ToolbarIcons.toolbarLinkChain} />}
                    size={'xlarge'}
                    placeholder={texts.link_dialog_link_placeholder}
                    value={src}
                    onChange={e => setSrc(e.target.value)}
                />
            )}

            {mode === UploadMode.FILE && (
                <div
                    ref={dropzoneRef}
                    onDragOver={onDragStart}
                    onDrop={handleDrop}
                    onDragLeave={onDragLeave}
                >
                    {!(fileName && src) ? (
                        <Uploader
                            defaultMargin={false}
                            file={null}
                            readOnly={false}
                            setFile={loadImage}
                            componentClasses={styles.dropzone}
                            accept="image/*"
                            placeholder={'Drop or upload image'}
                            icon={PriorityIcon.uploadCloud}
                        />
                    ) : (
                        <Input readOnly value={fileName} size="xlarge">
                            <Icon
                                style={{margin: '0 8px'}}
                                icon={PriorityIcon.closeBig}
                                size={'small'}
                                onClick={clearData}
                            />
                        </Input>
                    )}
                </div>
            )}

            <Flex justifyContent="flex-end">
                <Button
                    wideButton
                    size="large"
                    skin="primary"
                    disabled={!src}
                    onClick={insertImage}
                >
                    {texts.save_btn_title}
                </Button>
            </Flex>
        </Flex>
    );
}
